<template>
	<div class="comment-row" :class="{'comment-row-my':isMy}" @mouseenter="showOpts" @mouseleave="hideOpts" >
		<img v-if="!isMy" :src="'avatar/'+commentUser" class="chat-message-avatar" :title="userName(commentUser)">	
		<div :class="classComment">
			<div class="header">
				<span>
					<span v-if="!isMy && !form.forceInfo">{{ userName(commentUser) }} </span>
					<span v-html="form.message"></span>
				</span>
				<span>{{ lastModify }}</span>
			</div>
			<div class="body" :class="hasAttachment?'bottomLine':''">
				<sym-icon v-if="form.forCustomer" name="party" :title="env.msg('comment.forCustomer')" style="margin-right:4px" />
				<sym-icon v-if="form.forceInfo" :name="forceInfoIcon()" style="margin-right:4px"/>
				<span v-if="form.removed">{{ env.msg("note.removed")  }}</span>
				<span v-else v-html="form.content"></span>
			</div>
			<div v-if="!form.removed && hasAttachment">
				<div v-for="attach in form.attachments" class="attachment">
					<a :href="'openFile.do?id='+attach.id">
						<sym-icon :name="attach.icon" /> {{ attach.name }}
					</a>
				</div>
			</div>
		</div>
		<div v-if="visibleOpts" class="opts">
			<anchor-element tag="span" :chain="chain" style="margin-right: 4px;">
				<sym-icon name="more" class="link-like-fi boldCell" @click="chain.toggle"/>
			</anchor-element>
		</div>
	</div>
	<boat-element v-if="chain.visible" :chain="chain" tag="div" class="menu-panel boat tail-top-center">
		<div class="menu-panel-body">
			<a href="#" @click.stop.prevent="replyComment()" class="menu-item">
				<span class="mi mi-label"><sym-icon name="reply-mail" /> {{ env.msg("menu.replyMail") }}</span>
			</a>
			<a href="#" v-if="canEdit" @click.stop.prevent="removeComment()" class="menu-item">
				<span class="mi mi-label"><sym-icon name="trash" /> {{ env.msg("butt.delete") }}</span>
			</a>
		</div>
	</boat-element>
</template>

<script setup lang="ts">
import {ref, reactive, computed} from 'vue';
import {UserRef} from "@t/util/CommonTypes.ts";
import {Comment} from "@t/note/Comment.ts";
import {injectEnv, userInRole} from "@fd/Environment";
import type {SimplifiedUser} from "@t/user/SimplifiedUser";
import SymIcon from "@fd/helpers/SymIcon.vue";
import ApiError from "@/fd/helpers/ApiError.vue";
import AnchorElement from "@/ui/paneltree/AnchorElement.vue";
import BoatElement from "@/ui/paneltree/BoatElement.vue";
import {createChain} from "@/ui/paneltree/PanelTree";

	
const props = defineProps<{
	comment: Comment,
	users: SimplifiedUser[]
}>();

const env = injectEnv();
const visibleOpts = ref<boolean>(false);
const form = ref<Comment>({ ...props.comment });
const errors = reactive<ErrorMap>([]);
const emit = defineEmits(['reply']);


const chain = createChain({
	rule: {
		ah: 50, av: 100,
		bh: 50, bv: 0,
		x: 0, y: -4
	}
}, "opts");
	
const classComment = computed(() => {
	var clazz = "comment";
	if (form.value.removed) {
		clazz += " removed";
	}
	else if (form.value.forCustomer) {
		clazz += " for-customer";
	}
	else if (form.value.type === 'AUTO_GENERATED') {
		clazz += " system-note";
	}
	return clazz;
});	

const isMy = computed<boolean>(() => {
	return form.value.enteredBy === env.user.login;
});

const hasAttachment = computed<boolean>(() => {
	return form.value.attachments?.length>0;
});

const canEdit = computed<boolean>(() => {
	return !form.value.removed && !form.value.forceInfo &&
		(isMy.value || userInRole(env.user, "admin.app") || userInRole(env.user, "op.note.edit"));
});

const lastModify = computed<string>(() => {
	if (form.value.modified) {
		return new Date(form.value.modified).toLocaleDateString();
	} else {
		return new Date(form.value.entered).toLocaleDateString();
	}
});

const commentUser = computed<UserRef>(() => {
	return form.value.modifiedBy ? form.value.modifiedBy : form.value.enteredBy;
});


function hideOpts() {
	visibleOpts.value = false;
}

function showOpts() {
	if (!form.value.removed && !form.value.forceInfo) {
		visibleOpts.value = true;
	}
}

function userName(login: UserRef): string {
	let found = props.users.find(user => {
		if (typeof login === "string") return user.login === login;
		else return user.id === login;
	});
	return found ? found.name : login.toString();
}

function forceInfoIcon(): string {
	return form.value.type === 'DECLINED' ? "thumb-down{color:#FF0000}" : "thumb-up{color:#008000}";
}

function removeComment() {
	form.value.removed=true;
	chain.hide();
	//send on restApi
	let endpoint = "api/comments/"+form.value.id+"/remove";
	env.net.fetch(endpoint, "PATCH")
		.then((response) => {
			if (response.ok) {
				console.log("removed");
			}
		});
}

function replyComment() {
	chain.hide();
	emit("reply", form.value);
}

</script>

<style>
.comment-row {
	display:flex;
	flex-direction: row;
	padding-right: 4px;
	gap: 4px;
}

.comment-row-my {
	flex-direction: row-reverse;
}

.comment-row .opts {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px;
  font-size: 18px;
  opacity: 0.5;
}

.comment {
	padding: 8px 8px;
	color: var(--fg);
	background: var(--bg);
	border-radius: 8px;
	min-width: 120px; /* This is to prevent toolbar wrapping */
	max-width: 80%;
}
.comment p {
	line-height:1.5;
    margin-block-start:0;
	margin-block-end: 0;
}

.comment .header {
  display: flex;
  justify-content: space-between;
  align-items: center;  
  font-size:12px;
  margin-bottom:4px;
  gap: 16px;
}

.comment .body {
	overflow: auto;
}

.comment .attachment {
	padding-top: 4px;
}

.comment .attachment a {
	text-decoration: none;
}

</style>
